/* global console */
// ShapeEditor
// A lightweight, copyable drawing + custom editing layer for Google Maps shapes.
// - No google.maps.drawing
// - No built-in shape editor (no setEditable(true))
//
// Usage:
//   import ShapeEditor from './shape_editor'
//   const editor = new ShapeEditor(map, {
//     onComplete: (shape) => { ... },
//     onChange: (shape) => { ... },
//     onCancel: () => { ... },
//     onError: (err) => { ... }
//   });
//   editor.startDrawing('polygon'); // or 'rectangle' | 'circle'
//   editor.editShape(existingShape);
//
import { polygonCentroidOrBoundsCenter, isSimplePolygonLatLng } from './maps_helper'
import createShapeEditHandles from './shape_edit_handles'

export default class ShapeEditor {
    constructor(googleMap, options = {}) {
      if (!googleMap) {
        throw new Error('ShapeEditor requires a google.maps.Map instance');
      }

      this.map = googleMap;
      this.options = {
        onComplete: null,
        onChange: null,
        onCancel: null,
        onError: null,
        polygonOptions: { strokeWeight: 2, fillOpacity: 0.25, clickable: false },
        rectangleOptions: { strokeWeight: 2, fillOpacity: 0.15, clickable: false },
        circleOptions: { strokeWeight: 2, fillOpacity: 0.15, clickable: false },
        ghostLineOptions: { strokeWeight: 2, strokeOpacity: 0.6, clickable: false },
        polygonFinishMinVertices: 3,
        ...options,
      };

      this._mode = 'idle'; // 'idle' | 'drawing' | 'editing'
      this._drawingType = null; // 'polygon' | 'rectangle' | 'circle'
      this._activeShape = null; // shape being edited
      this._drawingShape = null; // shape being drawn (in progress)

      this._mapsListeners = [];
      this._domListeners = [];

      this._overlay = createShapeEditHandles(this.map, this);
      this._overlay.setMap(this.map);

      // Drawing-specific state
      this._polygonMouseLatLng = null;
      this._ghostEdge = null;
      this._ghostClose = null;
      this._rectAnchorLatLng = null;
      this._circleAnchorLatLng = null;
      this._circleStartTime = null;

      // Edit-specific state (center handle shown only while editing)
      this._editing = {
        shape: null,
        shapeType: null,
      };

      // Undo / redo history (edit mode only)
      this._undoStack = [];
      this._redoStack = [];
    }

    mode() {
      return this._mode;
    }

    startDrawing(type) {
      if (type !== 'polygon' && type !== 'rectangle' && type !== 'circle') {
        throw new Error(`Unsupported drawing type: ${type}`);
      }

      this.stopEditing();
      this.cancelDrawing();

      this._mode = 'drawing';
      this._drawingType = type;
      this._drawingShape = null;

      // reduce accidental map interactions during drawing
      if (this.map && this.map.setOptions) {
        if (type === 'polygon') {
          this.map.setOptions({ disableDoubleClickZoom: true });
        } else {
          this.map.setOptions({ draggable: false });
        }
      }

      if (type === 'polygon') this._startDrawingPolygon();
      if (type === 'rectangle') this._startDrawingRectangle();
      if (type === 'circle') this._startDrawingCircle();
    }

    cancelDrawing() {
      if (this._mode !== 'drawing') return;

      const cancelledShape = this._drawingShape;
      const cancelledType = this._drawingType;

      this._clearListeners();
      this._clearGhost();

      if (this._drawingShape && this._drawingShape.setMap) {
        this._drawingShape.setMap(null);
      }
      this._drawingShape = null;
      this._drawingType = null;
      this._mode = 'idle';
      this._polygonMouseLatLng = null;
      this._rectAnchorLatLng = null;
      this._circleAnchorLatLng = null;
      this._circleStartTime = null;

      this._overlay.clear(); // no handles while drawing is canceled
      this._restoreMapOptions();

      if (typeof this.options.onCancel === 'function') {
        // Extra arguments are safe in JS; callers can ignore if not needed.
        this.options.onCancel({ shape: cancelledShape, type: cancelledType });
      }
    }

    editShape(shape) {
      if (!shape) return;

      this.cancelDrawing();
      this.stopEditing();

      const shapeType = this._inferShapeType(shape);
      if (!shapeType) {
        this._error(new Error('Unsupported shape type for editing'));
        return;
      }

      this._mode = 'editing';
      this._activeShape = shape;
      this._editing.shape = shape;
      this._editing.shapeType = shapeType;
      this._clearUndo();

      // Center handle must only be shown in edit mode.
      this._overlay.attach(shape, { mode: 'editing', shapeType });
    }

    stopEditing() {
      if (this._mode !== 'editing') return;
      this._clearListeners();
      this._overlay.clear();
      this._activeShape = null;
      this._editing.shape = null;
      this._editing.shapeType = null;
      this._clearUndo();
      this._mode = 'idle';
      this._restoreMapOptions();
    }

    destroy() {
      this.cancelDrawing();
      this.stopEditing();
      this._clearUndo();
      if (this._overlay) {
        this._overlay.setMap(null);
        this._overlay = null;
      }
    }

    canUndo() {
      return this._mode === 'editing' && this._undoStack.length > 0;
    }

    canRedo() {
      return this._mode === 'editing' && this._redoStack.length > 0;
    }

    undo() {
      if (!this.canUndo()) return false;
      const shape = this._activeShape;
      if (!shape || !this._editing.shapeType) return false;

      const snapshot = this._undoStack.pop();
      if (!snapshot) return false;

      const currentSnap = this._snapshotShape(shape, this._editing.shapeType);
      this._redoStack.push(currentSnap);
      this._applySnapshot(shape, snapshot);
      if (this._overlay) this._overlay.requestDraw();
      this._change(shape);
      this._notifyUndoState();
      return true;
    }

    redo() {
      if (!this.canRedo()) return false;
      const shape = this._activeShape;
      if (!shape || !this._editing.shapeType) return false;

      const snapshot = this._redoStack.pop();
      if (!snapshot) return false;

      const currentSnap = this._snapshotShape(shape, this._editing.shapeType);
      this._undoStack.push(currentSnap);
      this._applySnapshot(shape, snapshot);
      if (this._overlay) this._overlay.requestDraw();
      this._change(shape);
      this._notifyUndoState();
      return true;
    }

    _clearUndo() {
      this._undoStack = [];
      this._redoStack = [];
      this._notifyUndoState();
    }

    _pushUndoSnapshot() {
      if (this._mode !== 'editing') return;
      if (!this._activeShape || !this._editing.shapeType) return;
      // New edit invalidates redo branch.
      this._redoStack = [];
      const snap = this._snapshotShape(this._activeShape, this._editing.shapeType);
      this._undoStack.push(snap);
      this._notifyUndoState();
      return snap;
    }

    _discardLastUndoSnapshot() {
      if (this._undoStack.length > 0) {
        this._undoStack.pop();
        this._notifyUndoState();
      }
    }

    _notifyUndoState() {
      if (typeof this.options.onUndoStateChange === 'function') {
        this.options.onUndoStateChange(this.canUndo(), this.canRedo());
      }
    }

    _snapshotShape(shape, shapeType) {
      if (shapeType === 'circle') {
        const c = shape.getCenter();
        return {
          type: 'circle',
          center: { lat: c.lat(), lng: c.lng() },
          radius: shape.getRadius()
        };
      }
      if (shapeType === 'rectangle') {
        const b = shape.getBounds();
        const ne = b.getNorthEast();
        const sw = b.getSouthWest();
        return {
          type: 'rectangle',
          bounds: { north: ne.lat(), east: ne.lng(), south: sw.lat(), west: sw.lng() }
        };
      }
      if (shapeType === 'polygon') {
        const path = shape.getPath();
        const pts = path && typeof path.getArray === 'function' ? path.getArray() : [];
        return {
          type: 'polygon',
          path: pts.map((p) => ({ lat: p.lat(), lng: p.lng() }))
        };
      }
      throw new Error(`Unsupported snapshot type: ${shapeType}`);
    }

    _applySnapshot(shape, snapshot) {
      if (snapshot.type === 'circle') {
        shape.setCenter(new google.maps.LatLng(snapshot.center.lat, snapshot.center.lng));
        shape.setRadius(snapshot.radius);
        return;
      }
      if (snapshot.type === 'rectangle') {
        const b = snapshot.bounds;
        const sw = new google.maps.LatLng(b.south, b.west);
        const ne = new google.maps.LatLng(b.north, b.east);
        shape.setBounds(new google.maps.LatLngBounds(sw, ne));
        return;
      }
      if (snapshot.type === 'polygon') {
        const pts = snapshot.path.map((p) => new google.maps.LatLng(p.lat, p.lng));
        shape.setPath(pts);
        return;
      }
    }

    _resetDrawingState() {
      this._clearListeners();
      this._clearGhost();
      this._overlay.clear();
      this._restoreMapOptions();

      this._mode = 'idle';
      this._drawingType = null;
      this._drawingShape = null;
      this._polygonMouseLatLng = null;
      this._rectAnchorLatLng = null;
      this._circleAnchorLatLng = null;
      this._circleStartTime = null;
    }

    _addDomListener(element, eventName, handler, capture = false) {
      if (!element || !element.addEventListener) return;
      element.addEventListener(eventName, handler, !!capture);
      this._domListeners.push({ element, eventName, handler, capture: !!capture });
    }

    /* -------------------------- Drawing modes -------------------------- */

    _startDrawingPolygon() {
      const polygon = new google.maps.Polygon({ ...this.options.polygonOptions });
      polygon.setMap(this.map);
      this._drawingShape = polygon;

      this._ghostEdge = new google.maps.Polyline({ map: this.map, ...this.options.ghostLineOptions });
      this._ghostClose = new google.maps.Polyline({ map: this.map, ...this.options.ghostLineOptions });

      // While drawing, make the polygon stroke match the ghost stroke so already-placed
      // edges don't change appearance after subsequent clicks.
      if (polygon.setOptions && this.options.ghostLineOptions) {
        const ghost = this.options.ghostLineOptions;
        polygon.setOptions({
          strokeColor: ghost.strokeColor,
          strokeOpacity: ghost.strokeOpacity,
          strokeWeight: ghost.strokeWeight
        });
      }

      // Show vertex dots (including first vertex for click-to-finish) while drawing.
      this._overlay.attach(polygon, { mode: 'drawing', shapeType: 'polygon' });

      // Click adds vertices.
      this._mapsListeners.push(this.map.addListener('click', (e) => {
        if (!e || !e.latLng) return;
        polygon.getPath().push(e.latLng);
        this._overlay.requestDraw();
        this._updateGhostForPolygon();
      }));

      // Mouse move updates ghost.
      this._mapsListeners.push(this.map.addListener('mousemove', (e) => {
        if (!e || !e.latLng) return;
        this._polygonMouseLatLng = e.latLng;
        this._updateGhostForPolygon();
      }));

      // Map dblclick may be swallowed by vertex handles; capture DOM dblclick too.
      this._mapsListeners.push(this.map.addListener('dblclick', (e) => {
        this._finishPolygon(e && e.domEvent ? e.domEvent : null, { fromDoubleClick: true });
      }));

      const mapDiv = this.map.getDiv && this.map.getDiv();
      if (mapDiv && mapDiv.addEventListener) {
        const domDblClick = (evt) => this._finishPolygon(evt, { fromDoubleClick: true });
        this._addDomListener(mapDiv, 'dblclick', domDblClick, true);
      }

      // Keyboard fallback: Enter finishes, Escape cancels.
      const keydown = (evt) => {
        if (this._mode !== 'drawing' || this._drawingType !== 'polygon') return;
        if (evt.key === 'Enter') this._finishPolygon(evt);
        if (evt.key === 'Escape') this.cancelDrawing();
      };
      this._addDomListener(window, 'keydown', keydown, true);

      // Allow clicking the first vertex to finish once we have enough points.
      this._overlay.setPolygonFinishHandler(() => this._finishPolygon(null));
    }

    _updateGhostForPolygon() {
      if (!this._ghostEdge || !this._ghostEdge.setPath) return;
      if (!this._drawingShape || !this._drawingShape.getPath) return;

      const path = this._drawingShape.getPath();
      const points = path.getArray ? path.getArray() : [];
      const mouse = this._polygonMouseLatLng;

      if (!mouse || points.length === 0) {
        this._ghostEdge.setPath([]);
        this._ghostClose.setPath([]);
        return;
      }

      const last = points[points.length - 1];
      this._ghostEdge.setPath([last, mouse]);

      // closing hint once there are at least 2 vertices: mouse -> first vertex
      if (points.length >= 2) {
        this._ghostClose.setPath([mouse, points[0]]);
      } else {
        this._ghostClose.setPath([]);
      }
    }

    _finishPolygon(domEvent, { fromDoubleClick = false } = {}) {
      if (domEvent && domEvent.preventDefault) domEvent.preventDefault();
      if (domEvent && domEvent.stopPropagation) domEvent.stopPropagation();

      if (!this._drawingShape || !this._drawingShape.getPath) return;
      const path = this._drawingShape.getPath();

      // A double-click fires two click events before the dblclick event, so the
      // second click adds an errant vertex right next to the intentional one.
      // Strip it before validating so the near-duplicate doesn't cause a
      // self-intersection failure.
      if (fromDoubleClick && path.getLength && path.getLength() > this.options.polygonFinishMinVertices) {
        path.removeAt(path.getLength() - 1);
      }

      const points = path.getArray ? path.getArray() : [];
      if (points.length < this.options.polygonFinishMinVertices) return;

      if (!isSimplePolygonLatLng(points)) {
        try { this._drawingShape.setMap(null); } catch (e) {}
        this._resetDrawingState();
        this._error(new Error('Invalid polygon: self-intersection'));
        return;
      }

      const completed = this._drawingShape;
      this._resetDrawingState();
      this._complete(completed);
    }

    _startDrawingRectangle() {
      let rectangle = null;

      this._mapsListeners.push(this.map.addListener('mousedown', (e) => {
        if (!e || !e.latLng) return;
        this._rectAnchorLatLng = e.latLng;
        const bounds = new google.maps.LatLngBounds(e.latLng, e.latLng);
        rectangle = new google.maps.Rectangle({ ...this.options.rectangleOptions, bounds });
        rectangle.setMap(this.map);
        this._drawingShape = rectangle;
      }));

      this._mapsListeners.push(this.map.addListener('mousemove', (e) => {
        if (!rectangle || !this._rectAnchorLatLng) return;
        if (!e || !e.latLng) return;
        const start = this._rectAnchorLatLng;
        const cur = e.latLng;

        const south = Math.min(start.lat(), cur.lat());
        const north = Math.max(start.lat(), cur.lat());
        const west = Math.min(start.lng(), cur.lng());
        const east = Math.max(start.lng(), cur.lng());

        const sw = new google.maps.LatLng(south, west);
        const ne = new google.maps.LatLng(north, east);
        rectangle.setBounds(new google.maps.LatLngBounds(sw, ne));
      }));

      this._mapsListeners.push(this.map.addListener('mouseup', () => {
        if (!rectangle) return;
        const completed = rectangle;
        rectangle = null;
        this._resetDrawingState();
        this._complete(completed);
      }));

      // Escape cancels
      const keydown = (evt) => {
        if (this._mode !== 'drawing' || this._drawingType !== 'rectangle') return;
        if (evt.key === 'Escape') this.cancelDrawing();
      };
      this._addDomListener(window, 'keydown', keydown, true);
    }

    _startDrawingCircle() {
      let circle = null;

      this._mapsListeners.push(this.map.addListener('mousedown', (e) => {
        if (!e || !e.latLng) return;
        this._circleAnchorLatLng = e.latLng;
        this._circleStartTime = new Date();
        circle = new google.maps.Circle({ ...this.options.circleOptions, center: e.latLng, radius: 1 });
        circle.setMap(this.map);
        this._drawingShape = circle;
        // Fire change immediately so callers can show radius marker right away.
        this._change(circle);
      }));

      this._mapsListeners.push(this.map.addListener('mousemove', (e) => {
        if (!circle || !this._circleAnchorLatLng) return;
        if (!e || !e.latLng) return;
        const radius = this._distanceBetween(this._circleAnchorLatLng, e.latLng);
        circle.setRadius(radius);
        // Update live while dragging (marker value + placement).
        this._change(circle);
      }));

      this._mapsListeners.push(this.map.addListener('mouseup', (e) => {
        if (!circle) return;
        if (!e || !e.latLng) return;

        // Avoid accidental clicks creating tiny circles.
        const dragTime = this._circleStartTime ? (new Date()).getTime() - this._circleStartTime.getTime() : 999999;
        const radius = this._distanceBetween(this._circleAnchorLatLng, e.latLng);
        if (radius <= 1.0) return;
        if (dragTime < 1000 && radius < 30) return;

        circle.setRadius(radius);
        this._change(circle);
        const completed = circle;
        circle = null;
        this._resetDrawingState();
        this._complete(completed);
      }));

      // Escape cancels
      const keydown = (evt) => {
        if (this._mode !== 'drawing' || this._drawingType !== 'circle') return;
        if (evt.key === 'Escape') this.cancelDrawing();
      };
      this._addDomListener(window, 'keydown', keydown, true);
    }

    /* -------------------------- Callbacks -------------------------- */

    _complete(shape) {
      try {
        if (typeof this.options.onComplete === 'function') {
          this.options.onComplete(shape);
        }
      } catch (err) {
        this._error(err);
      }
    }

    _change(shape) {
      try {
        if (typeof this.options.onChange === 'function') {
          this.options.onChange(shape);
        }
      } catch (err) {
        this._error(err);
      }
    }

    _error(err) {
      if (typeof this.options.onError === 'function') {
        this.options.onError(err);
      } else {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    }

    /* -------------------------- Helpers -------------------------- */

    _inferShapeType(shape) {
      if (shape instanceof google.maps.Circle) return 'circle';
      if (shape instanceof google.maps.Rectangle) return 'rectangle';
      if (shape instanceof google.maps.Polygon) return 'polygon';
      return null;
    }

    _shapeCenter(shape, shapeType) {
      if (shapeType === 'circle') return shape.getCenter();
      if (shapeType === 'rectangle') return shape.getBounds().getCenter();
      if (shapeType === 'polygon') return polygonCentroidOrBoundsCenter(shape);
      return null;
    }

    _distanceBetween(a, b) {
      const g = (typeof google !== 'undefined') ? google : null;
      if (g && g.maps && g.maps.geometry && g.maps.geometry.spherical && typeof g.maps.geometry.spherical.computeDistanceBetween === 'function') {
        return g.maps.geometry.spherical.computeDistanceBetween(a, b);
      }

      // Fallback: approximate haversine distance in meters.
      const R = 6371000;
      const toRad = (deg) => deg * Math.PI / 180;
      const dLat = toRad(b.lat() - a.lat());
      const dLng = toRad(b.lng() - a.lng());
      const lat1 = toRad(a.lat());
      const lat2 = toRad(b.lat());
      const sinDLat = Math.sin(dLat / 2);
      const sinDLng = Math.sin(dLng / 2);
      const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
      return 2 * R * Math.asin(Math.sqrt(h));
    }

    _clearGhost() {
      if (this._ghostEdge) {
        this._ghostEdge.setMap(null);
        this._ghostEdge = null;
      }
      if (this._ghostClose) {
        this._ghostClose.setMap(null);
        this._ghostClose = null;
      }
    }

    _restoreMapOptions() {
      if (!this.map || !this.map.setOptions) return;
      this.map.setOptions({ draggable: true, disableDoubleClickZoom: false });
    }

    _clearListeners() {
      if (this._mapsListeners && this._mapsListeners.length > 0) {
        this._mapsListeners.forEach((l) => google.maps.event.removeListener(l));
      }
      this._mapsListeners = [];

      if (this._domListeners && this._domListeners.length > 0) {
        this._domListeners.forEach(({ element, eventName, handler, capture }) => {
          if (element && element.removeEventListener) {
            element.removeEventListener(eventName, handler, !!capture);
          }
        });
      }
      this._domListeners = [];
    }

  }

